import { ORDER_STATUS } from "@miplatform/constants";
import { formatDate } from "@miplatform/utils";

export const ORDERS_DRAWER_WIDTH = 200;

export const ORDERS_DEFAULT_FIELD_WIDTH = 100;

export const CAPACITY_TRACKERS_STATUSES = new Set([
  ORDER_STATUS.NEW,
  ORDER_STATUS.RESCHEDULED,
]);

export const ORDERS_DATE_PICKER_CALENDER_COUNT = 2;

export const ORDERS_DATE_PICKER_MAX_SELECTION_DAYS = 184; // max amount of days in a 6 month period

export const ORDER_STATUS_FIELD = "Order Status";

export const ALL_OPTION = { label: "All", value: "All" };

export const NONE_OPTION = { label: "None", value: "None" };

export const ORDERS_TABS_NAMES = {
  NEW: "New",
  RESCHEDULE: "Reschedule",
  APPROVED: "Approved",
  CONFIRMED: "Confirmed",
  ASSIGNED: "Assigned",
  SCHEDULED: "Scheduled",
  HOLDS: "Holds",
  FOLLOW_UP: "Follow Up",
  CANCELLED: "Cancelled",
  COMPLETE: "Completed",
  ALL: "All",
};

export const TABS_WITH_COUNT_CHIPS = [
  ORDERS_TABS_NAMES.NEW,
  ORDERS_TABS_NAMES.RESCHEDULE,
  ORDERS_TABS_NAMES.HOLDS,
  ORDERS_TABS_NAMES.FOLLOW_UP,
];

export const ORDERS_DATE_RANGE_OPTIONS = {
  APPROVED_START_DATE: "approvedStartDate",
  ASSIGNED_DATE: "assignedDate",
  CANCELLED_DATE: "cancelledDate",
  COMPLETE_DATE: "completeDate",
};

export const ORDERS_SORT_OPTIONS = {
  RECEIVED_DATE: "receivedDate",
  APPROVED_START_DATE: "approvedStartDate",
  REQUESTED_START_DATE: "requestedStartDate",
  CONFIRMATION_DATE: "confirmationDate",
  ASSIGNED_DATE: "assignedDate",
  START_HOLD_DATE: "startHoldDate",
  FOLLOW_UP_DATE: "followUpDate",
  COMPLETE_DATE: "completeDate",
  REQUESTER: "requesterId desc",
  ADDRESS: "home/address/addressLine1",
  MODIFIED_ON: "modifiedOn desc",
  CANCELLED_DATE: "cancelledDate",
};

export const ORDERS_TABS = [
  {
    label: ORDERS_TABS_NAMES.ALL,
    value: ORDERS_TABS_NAMES.ALL,
    status: [
      ORDER_STATUS.COMPLETE,
      ORDER_STATUS.CANCELLED,
      ORDER_STATUS.IN_PROGRESS,
      ORDER_STATUS.ASSIGNED,
      ORDER_STATUS.CONFIRMED,
      ORDER_STATUS.HOLD,
      ORDER_STATUS.APPROVED,
      ORDER_STATUS.RESCHEDULED,
      ORDER_STATUS.NEW,
    ],
    sort: ORDERS_SORT_OPTIONS.MODIFIED_ON,
  },
  {
    label: ORDERS_TABS_NAMES.NEW,
    value: ORDERS_TABS_NAMES.NEW,
    status: [ORDER_STATUS.NEW],
    sort: ORDERS_SORT_OPTIONS.RECEIVED_DATE,
  },
  {
    label: ORDERS_TABS_NAMES.RESCHEDULE,
    value: ORDERS_TABS_NAMES.RESCHEDULE,
    sort: ORDERS_SORT_OPTIONS.REQUESTED_START_DATE,
    status: [ORDER_STATUS.RESCHEDULED],
  },
  {
    label: ORDERS_TABS_NAMES.SCHEDULED,
    value: ORDERS_TABS_NAMES.SCHEDULED,
    status: [ORDER_STATUS.APPROVED],
    sort: ORDERS_SORT_OPTIONS.APPROVED_START_DATE,
    dateRange: ORDERS_DATE_RANGE_OPTIONS.APPROVED_START_DATE,
  },
  {
    label: ORDERS_TABS_NAMES.CONFIRMED,
    value: ORDERS_TABS_NAMES.CONFIRMED,
    status: [ORDER_STATUS.CONFIRMED],
    sort: ORDERS_SORT_OPTIONS.APPROVED_START_DATE,
    dateRange: ORDERS_DATE_RANGE_OPTIONS.APPROVED_START_DATE,
  },
  {
    label: ORDERS_TABS_NAMES.ASSIGNED,
    value: ORDERS_TABS_NAMES.ASSIGNED,
    status: [ORDER_STATUS.ASSIGNED],
    sort: ORDERS_SORT_OPTIONS.ASSIGNED_DATE,
    dateRange: ORDERS_DATE_RANGE_OPTIONS.ASSIGNED_DATE,
  },
  {
    label: ORDERS_TABS_NAMES.COMPLETE,
    value: ORDERS_TABS_NAMES.COMPLETE,
    sort: ORDERS_SORT_OPTIONS.COMPLETE_DATE,
    status: [ORDER_STATUS.COMPLETE],
    dateRange: ORDERS_DATE_RANGE_OPTIONS.COMPLETE_DATE,
  },
  {
    label: ORDERS_TABS_NAMES.HOLDS,
    value: ORDERS_TABS_NAMES.HOLDS,
    status: [ORDER_STATUS.HOLD],
    sort: ORDERS_SORT_OPTIONS.FOLLOW_UP_DATE,
    customFilter: {
      followUpDate: {
        gt: new Date(),
      },
    },
  },
  {
    label: ORDERS_TABS_NAMES.FOLLOW_UP,
    value: ORDERS_TABS_NAMES.FOLLOW_UP,
    status: [ORDER_STATUS.HOLD],
    countStatus: [ORDER_STATUS.FOLLOW_UP],
    sort: ORDERS_SORT_OPTIONS.FOLLOW_UP_DATE,
    customFilter: {
      followUpDate: {
        le: new Date(),
      },
    },
  },
  {
    label: ORDERS_TABS_NAMES.CANCELLED,
    value: ORDERS_TABS_NAMES.CANCELLED,
    status: [ORDER_STATUS.CANCELLED],
    sort: ORDERS_SORT_OPTIONS.CANCELLED_DATE,
    dateRange: ORDERS_DATE_RANGE_OPTIONS.CANCELLED_DATE,
  },
];

export const ORDERS_COLUMNS_PRINT = [
  {
    label: "Address Line",
    field: "home.address.addressLine1",
    width: 150,
  },
  {
    label: "City",
    field: "home.address.city",
    width: 90,
  },
  {
    label: "Builder",
    field: "home.builderName",
    width: 120,
  },
  {
    label: "Community Name",
    field: "home.communityName",
    width: 130,
  },
  {
    label: "Req. Date",
    field: "requestedStartDate",
    width: 85,
  },
  {
    label: "Appr. Date",
    field: "approvedStartDate",
    width: 90,
  },
  {
    label: "Const. Mngr",
    field: "requesterName",
    width: 90,
  },
  {
    label: "Order",
    field: "orderTemplateShortName",
    width: 80,
  },
  { label: "Assignment", width: 200 },
];

export const ORDERS_ITEMS_PRINT_HEADER_FIELDS = [
  {
    label: "PO Number",
  },
  {
    label: "Order #",
    field: "orderNumber",
  },
];

export const ORDERS_ITEMS_PRINT_HIGHLIGHTED_HEADER_FIELD = {
  label: "Scheduled to be Performed:",
  valueGetter: ({ approvedStartDate }) =>
    approvedStartDate ? formatDate(approvedStartDate, "MM/DD/YYYY") : "-",
};

export const ORDERS_ITEMS_PRINT_SECTIONS = [
  {
    title: "Bill to",
    width: "49%",
    columns: [
      [
        { label: "Customer", field: "home.builderName" },
        {
          label: "Customer Point of Contact",
          valueGetter: ({ requesterName, requesterEmail }) => (
            <>
              {requesterName || "-"}
              {requesterEmail && (
                <>
                  <br />
                  {requesterEmail}
                </>
              )}
            </>
          ),
        },
      ],
      [
        {
          label: "Bill to Address",
          valueGetter: ({ builderAddress }) => (
            <>
              {builderAddress?.addressLine1}
              <br />
              {builderAddress?.addressLine2 && (
                <>
                  {builderAddress?.addressLine2}
                  <br />
                </>
              )}
              {builderAddress?.city &&
                `${builderAddress?.city}, ${builderAddress?.regionCode}, ${builderAddress?.postalCode}`}
            </>
          ),
        },
      ],
    ],
    rowTables: [
      [
        {
          label: "Reschedules",
          flex: 1,
          headerAlign: "left",
          align: "left",
          valueGetter: ({ previousRequestedStartDates = [] }) =>
            previousRequestedStartDates
              .map(
                (prevDate, i) =>
                  `${formatDate(prevDate, "MM/DD/YYYY")}${
                    i !== previousRequestedStartDates.length - 1 ? " | " : ""
                  }`
              )
              .join(""),
        },
      ],
    ],
  },
  {
    title: "Request Details",
    width: "49%",
    columns: [
      [
        {
          label: "Address",
          valueGetter: ({ home }) => (
            <>
              {home?.address?.addressLine1}
              <br />
              {home?.address?.addressLine2 && (
                <>
                  {home?.address?.addressLine2}
                  <br />
                </>
              )}
              {home?.address?.city &&
                `${home?.address?.city}, ${home?.address?.regionCode}, ${home?.address?.postalCode}`}
            </>
          ),
        },
        {
          label: "Power Type",
          field: "home.waterHeaterType",
        },
        {
          label: "Size",
          field: "home.complexity",
        },
      ],
      [
        {
          label: "Community",
          field: "home.communityName",
        },
        {
          label: "Order Created By",
          field: "orderCreatedByUser.displayName",
        },
      ],
    ],
  },
  {
    title: "Work Performed",
    getCellClassName: (params) => {
      if (
        params.field === ORDER_STATUS_FIELD &&
        params.value === ORDER_STATUS.APPROVED
      ) {
        return "blue bold";
      }

      if (params.field === ORDER_STATUS_FIELD) {
        return "grey bold";
      }

      return null;
    },
    rowTables: [
      [
        {
          label: "Type",
          field: "orderTemplateName",
          width: 130,
        },
        {
          label: "Service",
          defaultValue: "New Construction",
          width: 130,
        },
        {
          label: "Order Notes",
          field: "notes",
          defaultValue: "There is no notes for this order.",
          width: 300,
        },
        {
          label: "Billable",
          valueGetter: ({ orderTemplateIsPrebill }) =>
            orderTemplateIsPrebill ? "Yes" : "No",
          width: 90,
        },
        {
          label: "Order Status",
          field: "orderStatus",
          width: 120,
        },
      ],
    ],
  },
];
